import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { STYLES, buildModel, dielineLines, download, flatten, toDXF, toPDF, toSVG } from "./dieline";
import { Dieline3D } from "./Dieline3D";
import { Grid, Num, Pick, Result, UNIT_MM, card, convertVals, fmt } from "./ui";

function Preview2D({ d }) {
  const pad = 8;
  return (
    <svg data-testid="dieline-2d-preview" viewBox={`${-pad} ${-pad} ${d.width + pad * 2} ${d.height + pad * 2}`} style={{ width: "100%", height: 360, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12 }}>
      {d.lines.map((l, i) => <line key={i} x1={l.a[0]} y1={l.a[1]} x2={l.b[0]} y2={l.b[1]} stroke={l.type === "cut" ? "#E11D48" : "#2563EB"}
        strokeWidth={Math.max(d.width, d.height) / 400} strokeDasharray={l.type === "crease" ? `${Math.max(d.width, d.height) / 120} ${Math.max(d.width, d.height) / 200}` : undefined}/>)}
    </svg>
  );
}

export function DielineTool() {
  const [style, setStyle] = useState("rte");
  const [unit, setUnit] = useState("mm");
  const [dims, setDims] = useState({ L: 100, W: 50, D: 150 });
  const [fold, setFold] = useState(0.6);
  const k = UNIT_MM[unit];
  const [L, W, D] = [dims.L, dims.W, dims.D].map(v => Math.max(1, Number(v) || 0) * k);
  const valid = [dims.L, dims.W, dims.D].every(v => Number(v) > 0);
  const model = useMemo(() => buildModel(style, L, W, D), [style, L, W, D]);
  const d = useMemo(() => dielineLines(flatten(model)), [model]);
  useEffect(() => { localStorage.setItem("gpp_last_dieline", JSON.stringify({ w: d.width, h: d.height })); }, [d]);
  const name = `dieline-${style}-${Math.round(L)}x${Math.round(W)}x${Math.round(D)}mm`;
  const title = `${STYLES[style]} · L ${fmt(L, 1)} × W ${fmt(W, 1)} × D ${fmt(D, 1)} mm`;
  const exp = async (kind) => {
    if (kind === "svg") download(toSVG(d), `${name}.svg`, "image/svg+xml");
    if (kind === "dxf") download(toDXF(d), `${name}.dxf`, "application/dxf");
    if (kind === "pdf") download(await toPDF(d, title), `${name}.pdf`, "application/pdf");
    toast.success(`${kind.toUpperCase()} downloaded`);
  };
  const set = (key) => (v) => setDims({ ...dims, [key]: v });
  const isMailer = style === "mailer";
  return (
    <div data-testid="tool-dieline" style={{ display: "grid", gap: 16 }}>
      <div style={card}>
        <Grid>
          <Pick testid="dieline-style" label="Box style" value={style} onChange={(v) => { setStyle(v); if (v === "mailer" && style !== "mailer") setDims({ L: 250, W: 200, D: 80 }); if (v !== "mailer" && style === "mailer") setDims({ L: 100, W: 50, D: 150 }); }} options={Object.entries(STYLES)}/>
          <Pick testid="dieline-unit" label="Units" value={unit} onChange={(u) => { setDims(convertVals(dims, ["L", "W", "D"], unit, u)); setUnit(u); }} options={[["mm", "mm"], ["cm", "cm"], ["in", "inches"]]}/>
          <Num testid="dieline-length" label="Length (L)" suffix={unit} value={dims.L} onChange={set("L")}/>
          <Num testid="dieline-width" label="Width (W)" suffix={unit} value={dims.W} onChange={set("W")}/>
          <Num testid="dieline-depth" label={isMailer ? "Height (H)" : "Depth / height (D)"} suffix={unit} value={dims.D} onChange={set("D")}/>
        </Grid>
        <p style={{ fontSize: 11.5, color: "#64748b", margin: "10px 0 0" }}>Inside dimensions. Red = cut, blue dashed = crease. {style === "crash" ? "Crash-lock bottom flaps are drawn simplified; confirm lock geometry with your die-maker." : ""}</p>
      </div>
      {!valid ? <p data-testid="dieline-invalid" style={{ color: "#B91C1C" }}>Enter all three dimensions.</p> : (
        <>
          <Grid min={140}>
            <Result testid="dieline-flat-size" big label="Flat blank size" value={`${fmt(d.width, 1)} × ${fmt(d.height, 1)} mm`}/>
            <Result testid="dieline-flat-size-in" label="In inches" value={`${fmt(d.width / 25.4)} × ${fmt(d.height / 25.4)} in`}/>
            <Result testid="dieline-cut-length" label="Cut rule length" value={`${fmt(d.lines.filter(l => l.type === "cut").reduce((s, l) => s + Math.hypot(l.a[0] - l.b[0], l.a[1] - l.b[1]), 0) / 1000, 2)} m`}/>
            <Result testid="dieline-crease-length" label="Crease rule length" value={`${fmt(d.lines.filter(l => l.type === "crease").reduce((s, l) => s + Math.hypot(l.a[0] - l.b[0], l.a[1] - l.b[1]), 0) / 1000, 2)} m`}/>
          </Grid>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
            <div style={card}><b style={{ fontSize: 13 }}>2D dieline</b><div style={{ marginTop: 10 }}><Preview2D d={d}/></div></div>
            <div style={card}>
              <b style={{ fontSize: 13 }}>3D preview</b><span style={{ fontSize: 11.5, color: "#64748b" }}> · drag to rotate, scroll to zoom</span>
              <div style={{ marginTop: 10 }}><Dieline3D model={model} fold={fold} size={isMailer ? [L, D, W] : [L, W, D]}/></div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, marginTop: 10, color: "#475569" }}>Flat
                <input data-testid="dieline-fold-slider" type="range" min={0} max={1} step={0.01} value={fold} onChange={e => setFold(Number(e.target.value))} style={{ flex: 1 }}/>Folded
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["pdf", "svg", "dxf"].map(kd => <button key={kd} data-testid={`dieline-download-${kd}`} className="so-btn so-blue" onClick={() => exp(kd)}><Download size={14}/>Download {kd.toUpperCase()}</button>)}
          </div>
        </>
      )}
    </div>
  );
}
